import { useSelector } from 'react-redux';
import { ReduxRootState } from '@/types/store/ReduxRootState';
import { Button, ButtonProps, Text, useToast } from '@chakra-ui/react';
import { User } from '@/types/core/User';
import React, { useEffect, useState } from 'react';
import { fetchUser, inviteColleague } from '@/api/user';
import NextLink from 'next/link';
import { useGoogleTagManager } from '@components/app/hooks/useGTM';

type ColleagueRelationshipProps = ButtonProps & {
  user?: User;
  buttonVariant?: string;
};

export default function ColleagueRelationship({
  user,
  buttonVariant = 'tuileOutline',
  ...props
}: ColleagueRelationshipProps) {
  const toast = useToast();
  const { sendDataToGTM } = useGoogleTagManager();

  const currentUser = useSelector(
    (state: ReduxRootState) => state.authentication.user
  );
  const [displayedUser, setDisplayedUser] = useState<User | null>(user);
  const [sendingColleagueRequest, setSendingColleagueRequest] = useState(false);

  const isAColleague = displayedUser.colleagueState === 'colleague';
  const invitationReceived =
    displayedUser.colleagueState === 'invitationReceived';
  const invitationSent = displayedUser.colleagueState === 'invitationSent';

  const fetchAndUpdateUser = async () => {
    const updatedUser = await fetchUser(
      {
        fields: {
          user: `colleagueState:${
            currentUser ? currentUser.id : ''
          },isColleague:${currentUser ? currentUser.id : ''}`,
        },
      },
      displayedUser.id
    );
    if (!updatedUser) {
      return;
    }
    setDisplayedUser({
      ...displayedUser,
      colleagueState: updatedUser.colleagueState,
      isColleague: updatedUser.isColleague,
    });

    return updatedUser;
  };

  const onClickInviteColleague = async () => {
    setSendingColleagueRequest(true);
    const response = await inviteColleague(displayedUser);
    if (response.ok) {
      sendDataToGTM({
        data: {
          event: 'colleague_request',
          event_category: 'Membres',
          event_action: 'Devenir collègue',
          event_label: displayedUser.displayName,
          country: displayedUser.country.name,
        },
      });
      setSendingColleagueRequest(false);
      await fetchAndUpdateUser();
      toast({
        title: "Votre demande d'ajout à la liste des collègues a été envoyée.",
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } else {
      setSendingColleagueRequest(false);
      toast({
        title:
          "Une erreur est survenue lors de l'envoi de votre demande d'ajout à la liste des collègues.",
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (displayedUser?.id === currentUser?.id) {
    return null;
  }

  useEffect(() => {
    // We fetch colleagues fields if they are not present (e.g. on pages with algolia search results)
    if (displayedUser?.id && !displayedUser.colleagueState) {
      fetchAndUpdateUser();
    }
  }, [displayedUser.colleagueState]);

  return (
    <>
      {displayedUser && displayedUser.colleagueState === 'colleague' && (
        <NextLink
          href={{
            pathname: '/ecrire-membre',
            query: {
              user: displayedUser.id,
            },
          }}
          as={`/ecrire-membre?user=${displayedUser.id}`}
          passHref
        >
          <Button
            size={['xxs', 'sm']}
            variant={buttonVariant}
            flexBasis={{ base: 'calc(50% - .25rem)', sm: 'revert' }}
            {...props}
          >
            <Text
              as="span"
              letterSpacing={{ base: '-.5px', sm: 'revert' }}
              whiteSpace="nowrap"
            >
              Lui écrire
            </Text>
          </Button>
        </NextLink>
      )}
      {displayedUser &&
        !isAColleague &&
        !invitationReceived &&
        !invitationSent && (
          <Button
            size={['xxs', 'sm']}
            variant={
              buttonVariant == 'tournesolOutline' ? 'tournesol' : buttonVariant
            }
            as="a"
            cursor={sendingColleagueRequest ? 'not-allowed' : 'pointer'}
            onClick={() => onClickInviteColleague()}
            isLoading={sendingColleagueRequest}
            flexBasis={{ base: 'calc(50% - .25rem)', sm: 'revert' }}
            isDisabled={!displayedUser.colleagueState}
            {...props}
          >
            <Text
              as="span"
              letterSpacing={{ base: '-.5px', sm: 'revert' }}
              whiteSpace="nowrap"
            >
              Devenir collègue
            </Text>
          </Button>
        )}
      {displayedUser && !isAColleague && invitationReceived && (
        <Button
          size={['xxs', 'sm']}
          variant={buttonVariant}
          as="a"
          isDisabled
          flexBasis={{ base: 'calc(50% - .25rem)', sm: 'revert' }}
          {...props}
        >
          <Text as="span" letterSpacing={{ base: '-.5px', sm: 'revert' }}>
            Invitation envoyée
          </Text>
        </Button>
      )}
    </>
  );
}
